import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Clock, ArrowRight, Sparkles, Target } from "lucide-react";

/**
 * Personalized "Today's challenges" surface — driven by `user.focuses`.
 * Hides itself if the user hasn't picked any focuses yet (post-onboarding).
 */
export default function FocusedChallenges() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get("/dashboard/recommended");
        setData(r.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[focused-challenges] /dashboard/recommended fetch failed:", e?.response?.status, e?.message);
        // 401 etc. — silently hide
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!data?.focuses?.length) return null;
  const hasContent = (data.lessons?.length || 0) > 0 || (data.challenges?.length || 0) > 0;
  if (!hasContent) return null;

  const focusLabel = data.primary_focus
    ? data.primary_focus.replace("_", " ").replace(/\b\w/g, (m) => m.toUpperCase())
    : "your focus";

  return (
    <section className="mb-10" data-testid="dashboard-focused-challenges">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <span className="kicker" style={{ color: "var(--emerald, #10B981)" }}>
            <Target size={11} className="inline-block -mt-0.5 mr-1" />
            Tailored for you
          </span>
          <h2 className="font-display text-2xl md:text-3xl mt-1 leading-tight">
            Today's challenges for {focusLabel}
          </h2>
        </div>
        <Link
          to="/topics"
          className="text-sm font-medium text-[var(--accent)] hover:underline inline-flex items-center gap-1"
          data-testid="focused-browse-all"
        >
          Browse all <ArrowRight size={13} />
        </Link>
      </div>

      {/* Recommended in-app lessons */}
      {data.lessons?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          {data.lessons.slice(0, 3).map((l) => (
            <Link
              key={l.id}
              to={`/lesson/${l.id}`}
              className="brut p-4 group transition-all hover:-translate-y-0.5"
              data-testid={`focused-lesson-${l.id}`}
            >
              <div className="kicker" style={{ color: "var(--emerald, #10B981)" }}>
                {l.path}
              </div>
              <div className="font-display text-lg leading-tight mt-1">{l.title}</div>
              <div className="text-xs text-[var(--ink-3)] mt-1.5 flex items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <Clock size={11} /> 5 min
                </span>
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[var(--accent)] group-hover:gap-2 transition-all">
                Start <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* SEO challenges aligned with primary focus */}
      {data.challenges?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[var(--ink-3)] font-medium mb-2">
            More from your primary focus
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.challenges.map((c) => (
              <Link
                key={c.slug}
                to={c.url}
                className="brut p-4 group transition-all hover:-translate-y-0.5"
                style={{ borderTop: `3px solid ${c.accent}` }}
                data-testid={`focused-challenge-${c.slug}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl leading-none">{c.emoji}</span>
                  <span className="font-display text-sm md:text-base leading-tight">
                    {c.subtopic_name}
                  </span>
                </div>
                <div className="text-xs text-[var(--ink-3)] mt-2 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1">
                    <Sparkles size={11} /> {c.difficulty}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
